import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

const RetroWaveform = ({ height = 40, color = '#5fd4c4' }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const offsetRef = useRef(0);
    const [width, setWidth] = useState(180);

    // Interactive state
    const [isHovering, setIsHovering] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 }); // Normalized 0-1
    const [pulses, setPulses] = useState([]); // Array of pulse effects
    const [frequency, setFrequency] = useState(1);
    const [amplitude, setAmplitude] = useState(1);

    // Measure container width
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(container);
        setWidth(container.offsetWidth);

        return () => resizeObserver.disconnect();
    }, []);

    // Handle mouse move
    const handleMouseMove = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        setMousePos({ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) });

        // Mouse X controls frequency (0.5 to 2x), Y controls amplitude (0.5 to 1.5x)
        setFrequency(0.5 + x * 1.5);
        setAmplitude(0.5 + (1 - y) * 1);
    }, []);

    // Handle click - create pulse
    const handleClick = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;

        // Add a new pulse at click position
        const newPulse = {
            id: Date.now(),
            x: x * width,
            intensity: 1,
            createdAt: Date.now(),
        };

        setPulses(prev => [...prev, newPulse]);

        // Remove pulse after animation
        setTimeout(() => {
            setPulses(prev => prev.filter(p => p.id !== newPulse.id));
        }, 1000);
    }, [width]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || width <= 0) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        // Set up high-DPI canvas
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);

        const animate = () => {
            ctx.clearRect(0, 0, width, height);

            // Draw grid lines
            ctx.strokeStyle = isHovering ? 'rgba(63, 106, 100, 0.5)' : 'rgba(63, 106, 100, 0.3)';
            ctx.lineWidth = 0.5;
            for (let y = 0; y <= height; y += 10) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
            }
            for (let x = 0; x <= width; x += 20) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, height);
                ctx.stroke();
            }

            // Draw waveform
            const activeColor = isHovering ? '#b28c50' : color;
            ctx.strokeStyle = activeColor;
            ctx.lineWidth = isHovering ? 2 : 1.5;
            ctx.shadowColor = activeColor;
            ctx.shadowBlur = isHovering ? 12 : 8;
            ctx.beginPath();

            const centerY = height / 2;
            const baseAmplitude = height * 0.35 * amplitude;

            for (let x = 0; x <= width; x++) {
                // Calculate pulse effects at this x position
                let pulseEffect = 0;
                pulses.forEach(pulse => {
                    const age = (Date.now() - pulse.createdAt) / 1000;
                    const distance = Math.abs(x - pulse.x);
                    const waveSpeed = 200; // pixels per second
                    const wavePos = age * waveSpeed;

                    // Create expanding ring effect
                    if (distance < wavePos + 20 && distance > wavePos - 20) {
                        const ringIntensity = Math.max(0, 1 - age) * Math.exp(-Math.pow(distance - wavePos, 2) / 200);
                        pulseEffect += ringIntensity * 15;
                    }
                });

                // Composite wave with multiple frequencies, affected by mouse
                const freqMod = frequency;
                const wave1 = Math.sin((x + offsetRef.current) * 0.05 * freqMod) * baseAmplitude * 0.6;
                const wave2 = Math.sin((x + offsetRef.current * 1.5) * 0.08 * freqMod) * baseAmplitude * 0.3;
                const wave3 = Math.sin((x + offsetRef.current * 0.7) * 0.12 * freqMod) * baseAmplitude * 0.2;
                const noise = (Math.random() - 0.5) * (isHovering ? 1 : 2);

                const y = centerY + wave1 + wave2 + wave3 + noise + pulseEffect;

                if (x === 0) {
                    ctx.moveTo(x, Math.max(2, Math.min(height - 2, y)));
                } else {
                    ctx.lineTo(x, Math.max(2, Math.min(height - 2, y)));
                }
            }
            ctx.stroke();

            // Draw pulse origins
            pulses.forEach(pulse => {
                const age = (Date.now() - pulse.createdAt) / 1000;
                const opacity = Math.max(0, 1 - age);

                ctx.fillStyle = `rgba(178, 140, 80, ${opacity})`;
                ctx.shadowColor = '#b28c50';
                ctx.shadowBlur = 15 * opacity;
                ctx.beginPath();
                ctx.arc(pulse.x, centerY, 3 * (1 + age), 0, Math.PI * 2);
                ctx.fill();
            });

            // Draw center line
            ctx.strokeStyle = `rgba(95, 212, 196, ${isHovering ? 0.3 : 0.2})`;
            ctx.shadowBlur = 0;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, centerY);
            ctx.lineTo(width, centerY);
            ctx.stroke();

            // Draw crosshair at mouse position when hovering
            if (isHovering) {
                const crossX = mousePos.x * width;
                const crossY = mousePos.y * height;

                ctx.strokeStyle = 'rgba(178, 140, 80, 0.4)';
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(crossX, 0);
                ctx.lineTo(crossX, height);
                ctx.moveTo(0, crossY);
                ctx.lineTo(width, crossY);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            offsetRef.current += 2;
            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [width, height, color, isHovering, mousePos, frequency, amplitude, pulses]);

    return (
        <Box
            ref={containerRef}
            sx={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: `1px solid ${isHovering ? 'rgba(178, 140, 80, 0.6)' : 'rgba(63, 106, 100, 0.4)'}`,
                padding: 0.5,
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                cursor: 'crosshair',
                transition: 'border-color 0.2s ease',
            }}
        >
            <canvas
                ref={canvasRef}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => {
                    setIsHovering(false);
                    setFrequency(1);
                    setAmplitude(1);
                }}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                style={{
                    width: '100%',
                    height: height,
                    display: 'block',
                }}
            />

            {/* Readout display */}
            {isHovering && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 2,
                        right: 4,
                        display: 'flex',
                        gap: 1,
                        pointerEvents: 'none',
                    }}
                >
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.5rem',
                        color: '#b28c50',
                        textShadow: '0 0 4px rgba(178, 140, 80, 0.5)',
                    }}>
                        F:{frequency.toFixed(1)}x
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.5rem',
                        color: '#5fd4c4',
                        textShadow: '0 0 4px rgba(95, 212, 196, 0.5)',
                    }}>
                        A:{amplitude.toFixed(1)}x
                    </Typography>
                </Box>
            )}

            {/* Scanline overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.1) 0px, rgba(0,0,0,0.1) 1px, transparent 1px, transparent 2px)',
                }}
            />

            {/* Click hint */}
            {isHovering && (
                <Typography sx={{
                    position: 'absolute',
                    bottom: 2,
                    left: 4,
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.45rem',
                    color: '#666',
                    pointerEvents: 'none',
                }}>
                    click to ping
                </Typography>
            )}
        </Box>
    );
};

export default RetroWaveform;
